import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  FiMessageSquare,
  FiSend,
  FiX,
  FiZap,
  FiTrash2,
  FiChevronDown,
  FiChevronLeft,
  FiThumbsUp,
  FiThumbsDown,
  FiClock,
  FiBarChart2,
  FiUsers,
  FiAlertTriangle,
  FiCheckSquare,
  FiLoader,
  FiBookOpen
} from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import api from '../services/api'

const SESSION_STORAGE_KEY = 'ai_chat_session_id'

const iconMap = {
  FileCheck: FiCheckSquare,
  Clock: FiClock,
  Users: FiUsers,
  BarChart3: FiBarChart2,
  AlertTriangle: FiAlertTriangle
}

const clamp = (n, min, max) => Math.max(min, Math.min(max, n))

const formatDate = (value) => {
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString('pt-BR')
  } catch {
    return ''
  }
}

export default function SofiaChatWidget() {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const [sessionId, setSessionId] = useState(null)
  const [sessions, setSessions] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)

  const [quickPrompts, setQuickPrompts] = useState([])
  const [showPrompts, setShowPrompts] = useState(true)

  const [feedbackModal, setFeedbackModal] = useState({ show: false, messageId: null })
  const [feedbackComment, setFeedbackComment] = useState('')
  const [sendingFeedback, setSendingFeedback] = useState(false)

  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  // Load prompts + session cache
  useEffect(() => {
    const load = async () => {
      try {
        const promptsRes = await api.get('/api/ai/quick-prompts')
        if (Array.isArray(promptsRes.data)) {
          setQuickPrompts(promptsRes.data)
        }
      } catch (e) {
        // non-blocking
        console.error('Erro ao carregar quick-prompts:', e)
      }

      try {
        const saved = localStorage.getItem(SESSION_STORAGE_KEY)
        if (saved) {
          setSessionId(saved)
          await loadSessionMessages(saved)
        }
      } catch (e) {
        console.error('Erro ao carregar sessao salva:', e)
      }
    }

    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isOpen, showHistory])

  // Focus input
  useEffect(() => {
    if (isOpen && !showHistory) {
      inputRef.current?.focus()
    }
  }, [isOpen, showHistory])

  // Persist sessionId
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem(SESSION_STORAGE_KEY, sessionId)
    }
  }, [sessionId])

  const promptsByCategory = useMemo(() => {
    const grouped = {}
    for (const p of quickPrompts) {
      const cat = p.category || 'Sugestoes'
      if (!grouped[cat]) grouped[cat] = []
      grouped[cat].push(p)
    }
    return grouped
  }, [quickPrompts])

  const loadSessions = async () => {
    setLoadingHistory(true)
    try {
      const res = await api.get('/api/ai/chat/sessions', {
        params: { limit: 20 }
      })
      if (Array.isArray(res.data)) {
        setSessions(res.data)
      } else {
        setSessions([])
      }
    } catch (e) {
      console.error('Erro ao carregar sessoes:', e)
      setSessions([])
    } finally {
      setLoadingHistory(false)
    }
  }

  const loadSessionMessages = async (sid) => {
    try {
      const res = await api.get(`/api/ai/chat/sessions/${sid}/messages`)
      if (Array.isArray(res.data)) {
        const loaded = res.data.map((m) => ({
          id: String(m.id),
          role: m.role,
          content: m.content,
          timestamp: new Date(m.created_at),
          provider: m.provider,
          model: m.model,
          tokensUsed: m.tokens_used,
          feedback: m.feedback
            ? {
                id: String(m.feedback.id),
                feedback_type: m.feedback.feedback_type,
                comment: m.feedback.comment || null
              }
            : null
        }))

        setMessages(loaded)
        setShowPrompts(loaded.length === 0)
      }
    } catch (e) {
      console.error('Erro ao carregar mensagens da sessao:', e)
    }
  }

  const selectSession = async (session) => {
    setSessionId(session.session_id)
    await loadSessionMessages(session.session_id)
    setShowHistory(false)
    setShowPrompts(false)
  }

  const startNewChat = () => {
    setMessages([])
    setSessionId(null)
    localStorage.removeItem(SESSION_STORAGE_KEY)
    setShowPrompts(true)
    setShowHistory(false)
    setError(null)
  }

  const clearChat = async () => {
    if (sessionId) {
      try {
        await api.delete(`/api/ai/chat/history/${sessionId}`)
      } catch (e) {
        console.error('Erro ao limpar historico:', e)
      }
    }
    startNewChat()
  }

  const sendMessage = async (messageText) => {
    const text = (messageText || input).trim()
    if (!text || isLoading) return

    setError(null)
    setShowPrompts(false)

    const tempId = `temp-user-${Date.now()}`
    const tempUser = {
      id: tempId,
      role: 'user',
      content: text,
      timestamp: new Date()
    }

    setMessages((prev) => [...prev, tempUser])
    setInput('')
    setIsLoading(true)

    try {
      const res = await api.post('/api/ai/chat', {
        message: text,
        session_id: sessionId,
        include_context: true
      })

      if (res.data?.needsConfiguration) {
        setError(res.data?.error || 'Configure um provider de IA em Configuracoes')
        setIsLoading(false)
        return
      }

      const sid = res.data?.session_id || sessionId
      if (sid) setSessionId(sid)

      const assistantText = res.data?.message || res.data?.response?.message || ''
      const provider = res.data?.provider || res.data?.response?.provider
      const model = res.data?.model || res.data?.response?.model
      const tokensUsed = res.data?.tokens_used

      const userRealId = res.data?.user_message_id
      const assistantRealId = res.data?.assistant_message_id

      setMessages((prev) => {
        const updated = prev.filter((m) => m.id !== tempId)
        return [
          ...updated,
          {
            id: userRealId ? String(userRealId) : tempId,
            role: 'user',
            content: text,
            timestamp: new Date()
          },
          {
            id: assistantRealId ? String(assistantRealId) : String(Date.now() + 1),
            role: 'assistant',
            content: assistantText,
            timestamp: new Date(),
            provider,
            model,
            tokensUsed
          }
        ]
      })
    } catch (e) {
      const status = e?.response?.status
      const apiError = e?.response?.data?.error

      if (status === 403) {
        setError(apiError || 'Recurso de chat nao disponivel no seu plano.')
      } else {
        setError(apiError || 'Erro ao processar mensagem')
      }

      setMessages((prev) => prev.filter((m) => m.id !== tempId))

      if (e?.response?.data?.needsConfiguration) {
        // Direciona para configuracao
        navigate('/settings/ai')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const submitFeedback = async (messageId, feedbackType, comment) => {
    setSendingFeedback(true)
    try {
      await api.post('/api/ai/chat/feedback', {
        message_id: messageId,
        feedback_type: feedbackType,
        comment: comment || null
      })

      setMessages((prev) => prev.map((m) => (
        m.id === String(messageId)
          ? { ...m, feedback: { id: 'local', feedback_type: feedbackType, comment: comment || null } }
          : m
      )))

      setFeedbackModal({ show: false, messageId: null })
      setFeedbackComment('')
    } catch (e) {
      console.error('Erro ao enviar feedback:', e)
    } finally {
      setSendingFeedback(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <>
      {/* Floating button */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={clsx(
          'fixed bottom-6 right-6 p-4 rounded-full shadow-lg z-50 transition-all duration-300',
          isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100',
          'bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white'
        )}
        title="SofIA"
        aria-label="Abrir chat SofIA"
      >
        <FiZap className="w-6 h-6" />
      </button>

      {/* Widget */}
      <div
        className={clsx(
          'fixed bottom-6 right-6 w-[420px] max-w-[calc(100vw-3rem)] h-[600px] max-h-[calc(100vh-6rem)]',
          'bg-white dark:bg-gray-900 rounded-2xl shadow-2xl z-50 flex flex-col transition-all duration-300',
          'border border-gray-200 dark:border-gray-700',
          isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-t-2xl">
          <div className="flex items-center gap-3">
            {showHistory && (
              <button
                type="button"
                onClick={() => setShowHistory(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Voltar"
              >
                <FiChevronLeft className="w-5 h-5 text-white" />
              </button>
            )}

            <div className="p-2 bg-white/20 rounded-lg">
              <FiMessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white">
                {showHistory ? 'Historico' : 'SofIA'}
              </h3>
              <p className="text-xs text-white/70">
                {showHistory ? 'Conversas anteriores' : 'Sua assistente inteligente'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!showHistory && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setShowHistory(true)
                    loadSessions()
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  title="Ver historico"
                >
                  <FiBookOpen className="w-4 h-4 text-white" />
                </button>
                {messages.length > 0 && (
                  <button
                    type="button"
                    onClick={clearChat}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    title="Limpar conversa"
                  >
                    <FiTrash2 className="w-4 h-4 text-white" />
                  </button>
                )}
              </>
            )}

            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Fechar"
            >
              <FiX className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {showHistory ? (
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            <button
              type="button"
              onClick={startNewChat}
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors text-left"
            >
              <div className="p-2 rounded-lg bg-primary-600 text-white">
                <FiZap className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium text-primary-700 dark:text-primary-200">
                Nova conversa
              </span>
            </button>

            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <FiLoader className="w-6 h-6 animate-spin text-primary-600" />
              </div>
            ) : sessions.length === 0 ? (
              <p className="text-center text-gray-500 py-8 text-sm">
                Nenhuma conversa anterior
              </p>
            ) : (
              sessions.map((s) => (
                <button
                  key={s.session_id}
                  type="button"
                  onClick={() => selectSession(s)}
                  className={clsx(
                    'w-full flex items-start gap-3 p-3 rounded-xl transition-colors text-left',
                    sessionId === s.session_id
                      ? 'bg-secondary-50 dark:bg-secondary-900/20'
                      : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'
                  )}
                >
                  <div className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700">
                    <FiMessageSquare className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {s.title}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {s.message_count} mensagens • {formatDate(s.updated_at)}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        ) : (
          <>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {showPrompts && messages.length === 0 && (
                <div className="space-y-4">
                  <div className="text-center py-4">
                    <div className="inline-flex p-3 rounded-full bg-primary-100 dark:bg-primary-900/20 mb-3">
                      <FiZap className="w-8 h-8 text-primary-600 dark:text-primary-300" />
                    </div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Ola! Sou a SofIA
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Sua assistente inteligente para analise de produtividade. Pergunte sobre apps, sites, horarios e tendencias.
                    </p>
                  </div>

                  <div className="space-y-3">
                    {Object.entries(promptsByCategory).map(([category, prompts]) => (
                      <div key={category}>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-1">
                          {category}
                        </p>
                        <div className="space-y-2">
                          {prompts.map((p) => {
                            const Icon = iconMap[p.icon] || FiZap
                            return (
                              <button
                                key={p.id}
                                type="button"
                                onClick={() => sendMessage(p.prompt)}
                                className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left group"
                              >
                                <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/20 text-primary-600 dark:text-primary-300 group-hover:bg-primary-200 dark:group-hover:bg-primary-900/30 transition-colors">
                                  <Icon className="w-4 h-4" />
                                </div>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  {p.title}
                                </span>
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m) => (
                <div
                  key={m.id}
                  className={clsx('flex gap-3', m.role === 'user' ? 'flex-row-reverse' : '')}
                >
                  <div
                    className={clsx(
                      'p-2 rounded-full h-fit',
                      m.role === 'user' ? 'bg-secondary-600' : 'bg-primary-100 dark:bg-primary-900/20'
                    )}
                  >
                    <FiMessageSquare className={clsx('w-4 h-4', m.role === 'user' ? 'text-white' : 'text-primary-600 dark:text-primary-300')} />
                  </div>

                  <div className={clsx('flex-1 max-w-[80%]', m.role === 'user' ? 'text-right' : '')}>
                    <div
                      className={clsx(
                        'inline-block p-3 rounded-2xl',
                        m.role === 'user'
                          ? 'bg-secondary-600 text-white rounded-br-sm'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-bl-sm'
                      )}
                    >
                      <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                    </div>

                    {m.role === 'assistant' && (
                      <div className="flex items-center gap-2 mt-1">
                        {m.provider && (
                          <span className="text-xs text-gray-400">
                            {m.provider}
                            {m.tokensUsed ? ` • ${m.tokensUsed} tokens` : ''}
                          </span>
                        )}

                        {!m.feedback ? (
                          <div className="flex items-center gap-1 ml-auto">
                            <button
                              type="button"
                              onClick={() => submitFeedback(m.id, 'positive')}
                              className="p-1.5 rounded-full hover:bg-green-100 dark:hover:bg-green-900/20 transition-colors group"
                              title="Resposta util"
                            >
                              <FiThumbsUp className="w-4 h-4 text-gray-400 group-hover:text-green-600 transition-colors" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setFeedbackModal({ show: true, messageId: m.id })}
                              className="p-1.5 rounded-full hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors group"
                              title="Resposta nao util"
                            >
                              <FiThumbsDown className="w-4 h-4 text-gray-400 group-hover:text-red-600 transition-colors" />
                            </button>
                          </div>
                        ) : (
                          <div
                            className={clsx(
                              'ml-auto flex items-center gap-1.5 px-2 py-1 rounded-full',
                              m.feedback.feedback_type === 'positive'
                                ? 'bg-green-100 dark:bg-green-900/20'
                                : 'bg-red-100 dark:bg-red-900/20'
                            )}
                          >
                            {m.feedback.feedback_type === 'positive' ? (
                              <FiThumbsUp className="w-3.5 h-3.5 text-green-600" />
                            ) : (
                              <FiThumbsDown className="w-3.5 h-3.5 text-red-600" />
                            )}
                            <span
                              className={clsx(
                                'text-xs font-medium',
                                m.feedback.feedback_type === 'positive'
                                  ? 'text-green-700 dark:text-green-300'
                                  : 'text-red-700 dark:text-red-300'
                              )}
                            >
                              {m.feedback.feedback_type === 'positive' ? 'Util' : 'Nao util'}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex gap-3">
                  <div className="p-2 rounded-full bg-primary-100 dark:bg-primary-900/20 h-fit">
                    <FiMessageSquare className="w-4 h-4 text-primary-600 dark:text-primary-300" />
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-2xl rounded-bl-sm">
                    <div className="flex items-center gap-2">
                      <FiLoader className="w-4 h-4 animate-spin text-primary-600" />
                      <span className="text-sm text-gray-500 dark:text-gray-400">Analisando dados...</span>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 p-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              {messages.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowPrompts((v) => !v)}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 mb-2"
                >
                  <FiZap className="w-3 h-3" />
                  Ver sugestoes
                  <FiChevronDown className={clsx('w-3 h-3 transition-transform', showPrompts && 'rotate-180')} />
                </button>
              )}

              {showPrompts && messages.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {quickPrompts.slice(0, clamp(quickPrompts.length, 0, 4)).map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => sendMessage(p.prompt)}
                      disabled={isLoading}
                      className="px-3 py-1.5 text-xs rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors disabled:opacity-50"
                    >
                      {p.title}
                    </button>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Digite sua pergunta..."
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-800 border-none outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || isLoading}
                  className="p-3 rounded-xl bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <FiSend className="w-5 h-5" />
                </button>
              </div>

              <button
                type="button"
                onClick={() => navigate('/chat')}
                className="mt-3 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Abrir pagina completa de chat
              </button>
            </div>
          </>
        )}
      </div>

      {/* Feedback modal */}
      {feedbackModal.show && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/20">
                <FiThumbsDown className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Conte-nos mais</h3>
                <p className="text-sm text-gray-500">O que poderia ser melhorado?</p>
              </div>
            </div>

            <textarea
              value={feedbackComment}
              onChange={(e) => setFeedbackComment(e.target.value)}
              placeholder="Descreva o problema ou como a resposta poderia ser melhor..."
              className="w-full px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 border-none outline-none focus:ring-2 focus:ring-red-500 text-gray-900 dark:text-white placeholder-gray-500 resize-none"
              rows={4}
            />

            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={() => {
                  setFeedbackModal({ show: false, messageId: null })
                  setFeedbackComment('')
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  if (feedbackModal.messageId && feedbackComment.trim()) {
                    submitFeedback(feedbackModal.messageId, 'negative', feedbackComment.trim())
                  }
                }}
                disabled={!feedbackComment.trim() || sendingFeedback}
                className="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {sendingFeedback && <FiLoader className="w-4 h-4 animate-spin" />}
                Enviar Feedback
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
